/**
 * Orca Mode Capability Taxonomy
 *
 * Single source of truth for Orca's internal routing paths.
 *
 * Users do not select these paths. The composer stays in automatic routing;
 * Cortex chooses the backend endpoint per message, and the UI may disclose the
 * path that ran after the fact.
 *
 * ┌─────────────────────────────────────────────────────────────┐
 * │ Route         │ Endpoint(s)         │ Interaction Pattern   │
 * ├───────────────┼─────────────────────┼───────────────────────┤
 * │ Intelligence  │ orcaStream (SSE)    │ Streaming chat Q&A    │
 * │ Assistant     │ orcaAssist (SSE)    │ Tool use + artifacts  │
 * │ Agent         │ orcaAgent (SSE)     │ Multi-step autonomy   │
 * │ Auto          │ Routes dynamically  │ Default UI posture    │
 * └─────────────────────────────────────────────────────────────┘
 */

// ── Routing identifiers (used in state, URL params, backend config) ──

export type OrcaModeId = "auto" | "intelligence" | "assistant" | "agent";

// ── Mode definitions ──────────────────────────────────────────────

export interface OrcaMode {
  /** Stable machine identifier — used in state, events, backend routing */
  id: OrcaModeId;
  /** Display label for routing disclosure and internal diagnostics */
  label: string;
  /** One-line description shown in routing disclosure surfaces */
  description: string;
  /** Extended tagline for marketing / onboarding contexts */
  tagline: string;
  /** Backend Cloud Function endpoint name */
  endpoint: string | null;
  /** Lucide icon name (resolved in component) */
  iconName: "Zap" | "Brain" | "Handshake" | "Bot";
  /** Tier level: 0 = automatic routing posture, 1-3 = internal capability routes */
  tier: number;
  /**
   * DLG entitlement required to access this mode.
   *
   * Omitted for baseline modes (Intelligence, Auto) — available to any
   * provisioned user, matching backend `checkModeAccess` middleware.ts:104-106.
   * Strict modes (Assistant, Agent) require `"dynamic"`: an active Dynamic
   * subscription OR a Report buyer inside their starter window.
   */
  requiredEntitlement?: "report" | "dynamic";
  /** Whether this mode is implemented and usable */
  status: "active" | "planned" | "experimental";
  /** Interaction pattern description for backend system prompt */
  behaviorHint: string;
}

export const ORCA_MODES: Record<OrcaModeId, OrcaMode> = {
  auto: {
    id: "auto",
    label: "Auto",
    description: "Routes each message automatically",
    tagline: "Let Orca pick the right tool for the job.",
    endpoint: null,
    iconName: "Zap",
    tier: 0,
    // Baseline — backend short-circuits `auto` to allowed for any provisioned user.
    status: "active",
    behaviorHint:
      "Analyze the user's message intent and route to the appropriate mode: " +
      "factual questions → Intelligence, action requests → Assistant, " +
      "complex multi-step goals → Agent.",
  },

  intelligence: {
    id: "intelligence",
    label: "Orca Intelligence",
    description:
      "Intelligence-powered domain knowledge expert for immediate insights.",
    tagline:
      "Your domain knowledge expert in a Streaming Chat format — delivering immediate insights to your most pressing questions.",
    endpoint: "orcaStream",
    iconName: "Brain",
    tier: 1,
    // Baseline — backend short-circuits `intelligence` to allowed for any provisioned user.
    status: "active",
    behaviorHint:
      "Deep analysis and domain expertise. Provide thorough, well-reasoned " +
      "answers drawing on project context, zoning data, and ADU knowledge. " +
      "Streaming chat — no tool execution, no artifacts, just insight.",
  },

  assistant: {
    id: "assistant",
    label: "Orca Assistant",
    description:
      "Action-oriented support that works alongside you to get things done.",
    tagline:
      "Action-oriented support that works alongside you — preparing artifacts, running tools, and asking for review when work changes.",
    endpoint: "orcaAssist",
    iconName: "Handshake",
    tier: 2,
    requiredEntitlement: "dynamic",
    status: "active",
    behaviorHint:
      "Action-oriented and concise. Execute tools, create and update " +
      "artifacts (budgets, reports, checklists), open capabilities, and " +
      "take direct action. Confirm before destructive operations.",
  },

  agent: {
    id: "agent",
    label: "Orca Agent",
    description:
      "Advanced agentic planning and multi-step orchestration.",
    tagline:
      "Delegated AI that plans multi-step work, pauses at approval gates, and delivers reviewable results.",
    endpoint: "orcaAgent",
    iconName: "Bot",
    tier: 3,
    requiredEntitlement: "dynamic",
    status: "active",
    behaviorHint:
      "Multi-step planning and autonomous execution. Break complex goals " +
      "into steps, chain tools and sub-tasks, request approval at gates, " +
      "and deliver completed work products.",
  },
};

// ── Ordered list for UI rendering ─────────────────────────────────

export const ORCA_MODES_ORDERED: OrcaMode[] = [
  ORCA_MODES.auto,
  ORCA_MODES.intelligence,
  ORCA_MODES.assistant,
  ORCA_MODES.agent,
];

// ── Helpers ───────────────────────────────────────────────────────

/** Get the mode config by ID, defaulting to intelligence if unknown */
export function getOrcaMode(id: string): OrcaMode {
  return ORCA_MODES[id as OrcaModeId] ?? ORCA_MODES.intelligence;
}

/** Get the endpoint name for a given mode */
export function getEndpointForMode(id: string): string {
  return getOrcaMode(id).endpoint ?? "orcaStream";
}

/** Check if a mode is currently usable */
export function isModeActive(id: string): boolean {
  return getOrcaMode(id).status === "active";
}
